/**
 * RedoxPReleasePage — Ma & Wu 2008 ORP-driven sediment phosphorus release
 *
 * Sediment-bound iron-phosphorus complexes (Fe(III)-PO4) become unstable below a critical
 * redox potential, releasing soluble reactive phosphorus to the water column. Ma & Wu (2008)
 * fitted a sigmoidal model to Taihu Lake sediment cores:
 *     P_release = P_max × (1 − tanh((ORP − ORP_50)/k_ORP)) / 2
 *
 * Reference: Ma, S.N. & Wu, F.C. (2008) "Sediment phosphorus release under varying redox
 *            conditions in Taihu Lake" J. Environ. Sci. China 20(3): 269-275, Chinese Academy
 *            of Sciences, State Key Lab of Environmental Aquatic Chemistry.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Atom, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function RedoxPReleasePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('redox_p_release');
  const [orp, setOrp] = useState(50);         // mV
  const [pMax, setPMax] = useState(12);       // mg P m⁻² d⁻¹
  const [orp50, setOrp50] = useState(100);    // mV — half-release
  const [kOrp, setKOrp] = useState(60);       // mV — width

  const R = useMemo(() => {
    // Ma & Wu 2008 sigmoidal fit
    const fraction = 0.5 * (1 - Math.tanh((orp - orp50) / kOrp));
    const release = pMax * fraction;
    // Anoxic flag (ORP < -100 mV → full release zone)
    const regime = orp < -100 ? 'strongly anoxic' : orp < 50 ? 'anoxic' : orp < 200 ? 'transitional' : 'oxic';
    // Equivalent dissolved P concentration build-up over 30 days in 5 m hypolimnion
    const buildUp30d = (release * 30) / 5 / 1000;  // mg P/L → equivalent µg/L
    return { fraction, release, regime, buildUp30d };
  }, [orp, pMax, orp50, kOrp]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Atom} title="Module — Redox-driven Sediment P Release"
        subtitle="Ma & Wu 2008: ORP-controlled Fe-PO₄ dissolution from anoxic sediments"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('redox_p_release')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="redox_p_release" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Release fraction</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{(R.fraction * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">of P_max at this ORP</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Redox regime</div>
          <div className="text-lg font-bold text-amber-200 mt-1 uppercase">{R.regime}</div>
          <div className="text-xs text-slate-500 mt-1">ORP = {orp} mV</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">P flux from sediment</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{fmt(R.release, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">mg P m⁻² d⁻¹</div>
        </div>
        <div className="rounded-xl border-2 border-fuchsia-700 bg-fuchsia-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300">30-day SRP build-up</div>
          <div className="text-2xl font-bold text-fuchsia-200 mt-1">{fmt(R.buildUp30d * 1000, 0)}</div>
          <div className="text-xs text-slate-500 mt-1">µg P / L in 5 m hypolimnion</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">ORP: <strong className="text-slate-100">{orp} mV</strong></label>
          <input type="range" min={-300} max={400} step={10} value={orp} onChange={e => setOrp(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">P_max: <strong className="text-slate-100">{pMax} mg/m²/d</strong></label>
          <input type="range" min={1} max={30} step={0.5} value={pMax} onChange={e => setPMax(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">ORP_50: <strong className="text-slate-100">{orp50} mV</strong></label>
          <input type="range" min={-100} max={300} step={10} value={orp50} onChange={e => setOrp50(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">k_ORP: <strong className="text-slate-100">{kOrp} mV</strong></label>
          <input type="range" min={20} max={150} step={5} value={kOrp} onChange={e => setKOrp(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Mechanism &amp; engineering implication</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Iron-bound P (Fe(III)-PO₄) is stable while Fe stays oxidised → ORP &gt; 200 mV.</li>
          <li>As O₂ depletes, ORP falls; below ~+100 mV (ORP_50) Fe(III) reduces to Fe(II) and PO₄ dissociates.</li>
          <li>Released SRP diffuses upward; if stratification persists it accumulates in the hypolimnion.</li>
          <li>At autumn overturn the SRP-rich water mixes with surface, fuelling the next bloom.</li>
        </ol>
        <p className="mt-2">Aeration keeps ORP &gt; 250 mV at the sediment-water interface, suppressing this release by 80–95%.
        Module output feeds the Algal Dynamics page's phosphorus mass balance as an internal loading term.</p>
        <p className="italic text-slate-500 mt-2">Reference: Ma S.N. &amp; Wu F.C. (2008) J. Environ. Sci. China 20(3): 269-275, Chinese Academy of Sciences.</p>
      </div>
    </div>
  );
}
